import { useState, useCallback, useEffect } from 'react';
import styled from 'styled-components/macro';
import { useTranslation } from 'react-i18next';
import { useSetChain } from '@web3-onboard/react';
import { BigNumber } from 'ethers';

import useWeb3 from '../../contexts/Web3Context';
import { Market } from '../../contexts/MarketsContext';
import { useExitPositionInfo } from '../../hooks/useExitPositionInfo';
import { HydratedPosition } from '../../utils/data';
import { closePosition, getTraderFundingPayments } from '../../utils/clearingHouse';
import { ButtonXL } from '../Button';
import { Loader } from '../Loader';
import { SlippageInput } from '../SlippageInput';
import { Label3, Value5 } from '../Text';
import { targetChainId } from '../../data/chain';
import { formatDollarValue } from '../../utils/general';
import { Tooltip } from '../Tooltip';
import useUser from '../../contexts/UserContext';

const ContentContainer = styled.div`
  padding: 1.75rem;
  display: flex;
  flex-direction: column;
  max-width: 24rem;
`;

const StatContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

const StatLabel = styled(Label3)`
  margin: 0;
  display: flex;
  flex-direction: row;
  gap: 0.25rem;
  align-items: center;
`;

const StatValue = styled(Value5)<{ disabled?: boolean }>`
  margin: 0;
  opacity: ${({ disabled }) => (disabled ? '0.5' : '1')};
`;

const SlippageWrapper = styled.div`
  margin: 2rem 0;
`;

const ButtonWrapper = styled(ButtonXL)`
  min-height: 3.25rem;
`;

interface ClosePositionModalProps {
  market: Market;
  position: HydratedPosition;
  onTxFinish: () => void;
}

export function ClosePositionModalContent({
  market,
  position,
  onTxFinish,
}: ClosePositionModalProps) {
  const [, setChain] = useSetChain();
  const { chainConfig, signer, provider, address, networkError } = useWeb3();
  const { fundingPaymentsAcrossMarkets } = useUser();
  const { t } = useTranslation();

  const [slippage, setSlippage] = useState(0.1);
  const [loading, setLoading] = useState(false);
  const [fundingPayments, setFundingPayments] = useState<BigNumber>();

  const { exitPrice, minAmount, proposedAmount, tradingFee, pnl } = useExitPositionInfo({
    currentPosition: position.ref,
    slippage,
    signerOrProvider: signer ?? provider,
    market,
    chainConfig,
    user: address,
  });

  const handleClosePosition = useCallback(async () => {
    if (chainConfig && signer && provider && !loading) {
      setLoading(true);
      try {
        if (
          networkError &&
          !(await setChain({
            chainId: targetChainId,
          }))
        ) {
          throw new Error('User refused to switch chains');
        }
        if (proposedAmount === undefined) throw new Error('proposedAmount is undefined');
        await closePosition({
          chainConfig,
          signer,
          proposedAmount,
          minAmount,
          marketId: position.ref.market.id,
          direction: position.ref.direction,
        });
        onTxFinish();
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    }
  }, [
    position,
    onTxFinish,
    chainConfig,
    signer,
    provider,
    loading,
    minAmount,
    networkError,
    setChain,
    proposedAmount,
  ]);

  useEffect(() => {
    let subscribed = true;
    if (address && provider !== undefined) {
      getTraderFundingPayments({
        signerOrProvider: provider,
        chainConfig,
        userAddress: address,
        marketId: market.id,
      })
        .then((res) => {
          if (subscribed) {
            setFundingPayments(res);
          }
        })
        .catch(console.error);
    }
    return () => {
      subscribed = false;
    };
  }, [market, address, signer, provider, chainConfig]);

  return (
    <ContentContainer>
      <StatContainer>
        <StatLabel>{t('Exit price')}</StatLabel>
        <StatValue>
          {exitPrice && market && formatDollarValue(exitPrice, 18, false, market.displayDecimals)}
        </StatValue>
      </StatContainer>
      <StatContainer>
        <StatLabel>{t('Position size')}</StatLabel>
        <StatValue>{position.size}</StatValue>
      </StatContainer>
      <StatContainer>
        <StatLabel>{t('Funding payments')}</StatLabel>
        <StatValue value={fundingPayments ? Number(fundingPayments.toString()) : null}>
          {fundingPayments ? formatDollarValue(fundingPayments, 18, false, 2) : '-'}
        </StatValue>
      </StatContainer>
      <StatContainer>
        <StatLabel>
          {t('Realized PnL')}{' '}
          <Tooltip size="s" position="top" offset={0.15}>
            {t('Realized PnL is calculated using the execution price of the trade.')}
          </Tooltip>
        </StatLabel>
        <StatValue value={pnl ? Number(pnl.toString()) : null}>
          {pnl ? formatDollarValue(pnl, 18, false, 2) : '0'}
        </StatValue>
      </StatContainer>
      <StatContainer>
        <StatLabel>{t('Closing fee')}</StatLabel>
        <StatValue value={Number(tradingFee.toString())}>
          {formatDollarValue(tradingFee, 18, false, 2)}
        </StatValue>
      </StatContainer>
      <StatContainer>
        <StatLabel>{t('Total PnL')}</StatLabel>
        <StatValue
          value={
            pnl && fundingPayments && tradingFee
              ? Number(tradingFee.add(pnl).add(fundingPayments).toString())
              : null
          }
        >
          {pnl && fundingPayments && tradingFee
            ? formatDollarValue(tradingFee.add(pnl).add(fundingPayments), 18, false, 2)
            : '-'}
        </StatValue>
      </StatContainer>
      {fundingPaymentsAcrossMarkets &&
        fundingPayments &&
        !fundingPaymentsAcrossMarkets.eq(fundingPayments) && (
          <StatContainer>
            <StatLabel>
              {t('Other funding payments')}
              <Tooltip size="s" position="bottom">
                {t('All funding payments across markets will be settled during this transaction.')}
              </Tooltip>
            </StatLabel>
            <StatValue
              disabled
              value={
                fundingPaymentsAcrossMarkets
                  ? Number(fundingPaymentsAcrossMarkets.sub(fundingPayments))
                  : null
              }
            >
              {fundingPaymentsAcrossMarkets
                ? formatDollarValue(fundingPaymentsAcrossMarkets.sub(fundingPayments), 18, false, 2)
                : '-'}
            </StatValue>
          </StatContainer>
        )}
      <SlippageWrapper>
        <SlippageInput options={[0.1, 0.5, 1, 1.5]} onChange={setSlippage} max={50} />
      </SlippageWrapper>
      <ButtonWrapper onClick={handleClosePosition}>
        {loading ? <Loader /> : t('Confirm close')}
      </ButtonWrapper>
    </ContentContainer>
  );
}
